import React, { useState } from 'react';
import { Badge, Button, InlineStack, Modal, Text, Thumbnail } from '@shopify/polaris';
import { renderMentions } from './mentions';
import ImageLightbox from '../ImageLightbox';

const fmt = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function initials(name) { return (name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase(); }

export default function CommentEntry({ c, onEdit, onDelete, onReply, highlight, compact }) {
    const [confirm, setConfirm] = useState(false);
    const [open, setOpen] = useState(null);
    const src = c.origin === 'shopify' ? 'shop' : 'ops';
    const mirror = c.origin === 'opshub' && (
        c.mirror_status === 'mirrored' ? <Badge tone="success" size="small">Also on Shopify</Badge>
        : c.mirror_status === 'failed' ? <Badge tone="warning" size="small">{/edited in OpsHub/.test(c.mirror_error || '') ? 'Shopify copy out of date' : 'Not on Shopify'}</Badge>
        : null);
    return (
        <div id={`comment-${c.id}`} className={`cm-entry cm-entry--${src}${highlight ? ' cm-entry--hl' : ''}${c.deleted_at ? ' cm-entry--deleted' : ''}`}>
            {c.author_avatar_url ? <img className="cm-avatar cm-avatar--img" src={c.author_avatar_url} alt="" />
                                 : <span className={`cm-avatar cm-avatar--${src}`}>{initials(c.author_name)}</span>}
            <div className="cm-entry__body">
                <InlineStack gap="150" blockAlign="center" wrap>
                    <Text as="span" fontWeight="semibold">{c.author_name}</Text>
                    <Badge tone={src === 'ops' ? 'info' : 'success'} size="small">{src === 'ops' ? 'OpsHub' : 'Shopify'}</Badge>
                    {mirror}
                    <Text as="span" tone="subdued" variant="bodySm">{fmt(c.created_at)}{c.edited_at ? ' · edited' : ''}</Text>
                    {c.deleted_at && <Badge size="small">Deleted by {c.deleted_by}</Badge>}
                </InlineStack>
                <p className="cm-entry__text">{renderMentions(c.body)}</p>
                {c.images?.length > 0 && (
                    <InlineStack gap="200">
                        {c.images.map((im, i) => (
                            <button key={im.id || im.url} type="button" className="cm-thumb cm-thumb--btn" onClick={() => setOpen(i)}>
                                <Thumbnail source={im.thumb_url || im.url} alt={im.name || ''} size="medium" />
                            </button>
                        ))}
                    </InlineStack>
                )}
                {!compact && !c.deleted_at && (
                    <InlineStack gap="300">
                        {onReply && <Button variant="plain" size="slim" onClick={() => onReply(c)}>Reply</Button>}
                        {c.can_edit && <Button variant="plain" size="slim" onClick={() => onEdit(c)}>Edit</Button>}
                        {c.can_delete && <Button variant="plain" size="slim" tone="critical" onClick={() => setConfirm(true)}>Delete</Button>}
                    </InlineStack>
                )}
            </div>
            {c.images?.length > 0 && (
                <ImageLightbox open={open !== null} onClose={() => setOpen(null)}
                    images={c.images.map((im, i) => ({ key: String(i), label: im.name || `Image ${i + 1}`, src: im.url }))}
                    initialKey={String(open ?? 0)} />
            )}
            <Modal open={confirm} onClose={() => setConfirm(false)} title="Delete this comment?"
                   primaryAction={{ content: 'Delete', destructive: true, onAction: () => { setConfirm(false); onDelete(c); } }}
                   secondaryActions={[{ content: 'Cancel', onAction: () => setConfirm(false) }]}>
                <Modal.Section><Text as="p">It is removed from OpsHub and from anyone's Inbox{c.mirror_status === 'mirrored' ? ', and from the Shopify timeline when the session allows' : ''}.</Text></Modal.Section>
            </Modal>
        </div>
    );
}
